import {
  type AdminCategoryQuery,
  type AdminProductQuery,
  type AdminProductRow,
  type CategoryWithCount,
  type InventoryItem,
  type Paginated,
  type PaginatedWithCounts,
  type Product,
  type ProductStatus,
  type StockStatus,
} from "@milqit/shared-types";
import type {
  AisleLandingValues,
  CategoryFormValues,
  ProductFormValues,
  StockAdjustmentValues,
} from "@milqit/validations";

import { apiFetch, apiFetchOrNull, qs } from "./client";

/* Products ---------------------------------------------------------------- */

export type ProductRow = AdminProductRow;
export type ProductListQuery = AdminProductQuery;

export async function listProducts(
  query: ProductListQuery,
): Promise<PaginatedWithCounts<ProductRow, ProductStatus>> {
  return apiFetch(`/admin/products${qs({ ...query })}`);
}

export async function getProduct(id: string): Promise<ProductRow | null> {
  return apiFetchOrNull(`/admin/products/${id}`);
}

export async function createProduct(values: ProductFormValues): Promise<Product> {
  return apiFetch(`/admin/products`, { method: "POST", body: JSON.stringify(values) });
}

export async function updateProduct(id: string, values: ProductFormValues): Promise<Product> {
  return apiFetch(`/admin/products/${id}`, { method: "PUT", body: JSON.stringify(values) });
}

export async function deleteProduct(id: string): Promise<void> {
  return apiFetch(`/admin/products/${id}`, { method: "DELETE" });
}

/* Categories ------------------------------------------------------------- */

export async function listCategories(query: AdminCategoryQuery = {}): Promise<CategoryWithCount[]> {
  return apiFetch(`/admin/categories${qs({ ...query })}`);
}

export async function saveCategory(values: CategoryFormValues, id?: string): Promise<void> {
  return apiFetch(id ? `/admin/categories/${id}` : `/admin/categories`, {
    method: id ? "PUT" : "POST",
    body: JSON.stringify(values),
  });
}

/** An aisle's page header (and promo card), or null to use its name and photo. */
export async function saveAisleLanding(
  id: string,
  landing: AisleLandingValues | null,
): Promise<void> {
  return apiFetch(`/admin/categories/${id}/landing`, {
    method: "PUT",
    body: JSON.stringify({ landing }),
  });
}

export async function deleteCategory(id: string): Promise<void> {
  return apiFetch(`/admin/categories/${id}`, { method: "DELETE" });
}

/* Inventory -------------------------------------------------------------- */

export interface InventoryRow extends InventoryItem {
  product: Product;
  available: number;
  status: StockStatus;
}

export interface InventoryQuery {
  q?: string;
  status?: StockStatus | "all";
  warehouse?: string;
  page: number;
  pageSize: number;
}

export async function listInventory(
  query: InventoryQuery,
): Promise<Paginated<InventoryRow> & { counts: Record<StockStatus, number> }> {
  return apiFetch(`/admin/inventory${qs({ ...query })}`);
}

export async function adjustStock(
  productId: string,
  values: StockAdjustmentValues,
): Promise<InventoryItem> {
  return apiFetch(`/admin/inventory/${productId}/adjust`, {
    method: "POST",
    body: JSON.stringify(values),
  });
}
